import { useCallback } from 'react';
import useApi from './useApi';
import { ProcedureDetails } from '../types/ProcedureTypes';
import { useAppSettings } from '../hooks/useAppSettings';

export interface ProcedureUpdateResponse {
    success: boolean;
    data: any;
    message: string;
}

/**
 * Custom hook for updating procedure data
 * Uses PUT endpoint: /procedure/put
 *
 * @example
 * const { updateProcedure, isLoading, error, response } = useUpdateProcedure();
 *
 * const handleSave = async () => {
 *   try:
 *     await updateProcedure(procedureData);
 *     // Handle success
 *   } catch (err) {
 *     // Handle error
 *   }
 * };
 */
const useUpdateProcedure = () => {
    const { state, postApiData } = useApi();
    const { settings: appSettings } = useAppSettings();

    /**
     * Updates a procedure via the PUT API endpoint
     * @param procedureData - Complete procedure data or API payload
     * @returns Promise<ProcedureUpdateResponse>
     */
    const updateProcedure = useCallback(
        async (procedureData: ProcedureDetails | any): Promise<ProcedureUpdateResponse> => {
            if (!procedureData || procedureData.id === undefined || procedureData.id === null) {
                throw new Error('Procedure data with valid ID is required');
            }

            try {
                const apiUrl = appSettings?.api;
                if (!apiUrl) {
                    throw new Error('API URL not configured in app settings');
                }

                // Ensure no double slashes in URL
                const endpoint = `${apiUrl}/UpsertProcedure`;

                const response = await postApiData({
                    endpoint,
                    data: procedureData,
                    config: { params: { releaseId: appSettings?.releaseId || '' } },
                });

                return {
                    success: true,
                    data: response,
                    message: 'Procedure updated successfully',
                };
            } catch (error: any) {
                console.error('Failed to update procedure:', error);

                const errorMessage = error.response?.data?.message || error.response?.statusText || error.message || 'Failed to update procedure';

                throw new Error(errorMessage);
            }
        },
        [appSettings, postApiData]
    );

    /**
     * Updates a specific field of the procedure
     * Useful for partial updates without sending the entire payload
     *
     * @param procedureId - ID of the procedure to update
     * @param updates - Partial procedure data to update
     * @param fullProcedure - Complete current procedure data (required for API)
     */
    const updateProcedureField = useCallback(
        async (procedureId: number, updates: Partial<ProcedureDetails>, fullProcedure: ProcedureDetails | any): Promise<ProcedureUpdateResponse> => {
            // Merge updates with full procedure data
            const updatedProcedure = {
                ...fullProcedure,
                ...updates,
                Id: procedureId,
                LastUpdatedUtc: new Date().toISOString(),
            };

            return updateProcedure(updatedProcedure);
        },
        [updateProcedure]
    );

    return {
        state,
        updateProcedure,
        updateProcedureField,
        isLoading: state.isLoading,
        error: state.error,
        response: state.result as ProcedureUpdateResponse | null,
    };
};

export default useUpdateProcedure;
